/**
 * Transaction Simulator
 *
 * Simulates blockchain transactions with realistic behavior:
 * - Loading states
 * - Delays (1-3 seconds)
 * - Success/failure (95% success rate)
 * - Transaction signatures (simulated)
 *
 * This module can be easily replaced with real Solana transactions
 * without affecting the rest of the application.
 */

import { getRandomDelay, simulateTransactionSuccess } from './randomness';
import { TRANSACTION_CONFIG } from '../config/constants';

/**
 * Transaction status enum
 */
export const TransactionStatus = {
    PENDING: 'pending',
    CONFIRMING: 'confirming',
    SUCCESS: 'success',
    FAILED: 'failed',
};

/**
 * Transaction type enum
 */
export const TransactionType = {
    DEPLOY: 'deploy',
    CLAIM: 'claim',
    STAKE: 'stake',
    UNSTAKE: 'unstake',
};

/**
 * Generate a simulated transaction signature
 *
 * @returns {string} Simulated transaction signature
 */
function generateSimulatedSignature() {
    const chars = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
    let signature = '';
    for (let i = 0; i < 88; i++) {
        signature += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return signature;
}

/**
 * Simulate a blockchain transaction
 *
 * @param {Object} params - Transaction parameters
 * @param {string} params.type - Transaction type
 * @param {number} params.amount - Amount (SOL or MINER)
 * @param {string} params.description - Transaction description
 * @returns {Promise<Object>} Transaction result
 */
export async function simulateTransaction(params) {
    const {
        type = TransactionType.DEPLOY,
        amount = 0,
        description = 'Transaction',
    } = params;

    // Create transaction object
    const transaction = {
        signature: generateSimulatedSignature(),
        type,
        amount,
        description,
        status: TransactionStatus.PENDING,
        timestamp: Date.now(),
    };

    // Simulate network delay
    const delay = getRandomDelay(
        TRANSACTION_CONFIG.MIN_DELAY,
        TRANSACTION_CONFIG.MAX_DELAY
    );

    // Update to confirming status
    transaction.status = TransactionStatus.CONFIRMING;

    // Wait for "confirmation"
    await new Promise(resolve => setTimeout(resolve, delay));

    // Check if transaction succeeds
    const success = simulateTransactionSuccess(TRANSACTION_CONFIG.SUCCESS_RATE);

    if (success) {
        transaction.status = TransactionStatus.SUCCESS;
    } else {
        transaction.status = TransactionStatus.FAILED;
        transaction.error = getRandomError();
    }

    return transaction;
}

/**
 * Get a random error message (for simulation)
 *
 * @returns {string} Error message
 */
function getRandomError() {
    const errors = [
        'Transaction simulation failed',
        'Insufficient SOL for transaction fee',
        'Blockhash not found',
        'Network congestion - please try again',
        'Transaction timeout',
    ];
    return errors[Math.floor(Math.random() * errors.length)];
}

/**
 * Simulate Deploy SOL transaction
 *
 * @param {number} amount - Amount of SOL to deploy
 * @param {Array} blocks - Array of block numbers
 * @returns {Promise<Object>} Transaction result
 */
export async function simulateDeployTransaction(amount, blocks) {
    return simulateTransaction({
        type: TransactionType.DEPLOY,
        amount,
        description: `Deploy ${amount.toFixed(2)} SOL to ${blocks.length} block${blocks.length > 1 ? 's' : ''}`,
    });
}

/**
 * Simulate Claim Rewards transaction
 *
 * @param {number} minerAmount - Amount of MINER to claim
 * @param {number} solAmount - Amount of SOL to claim
 * @returns {Promise<Object>} Transaction result
 */
export async function simulateClaimTransaction(minerAmount, solAmount) {
    const parts = [];
    if (minerAmount > 0) parts.push(`${minerAmount.toFixed(3)} MINER`);
    if (solAmount > 0) parts.push(`${solAmount.toFixed(4)} SOL`);

    return simulateTransaction({
        type: TransactionType.CLAIM,
        amount: minerAmount + solAmount,
        description: `Claim ${parts.join(' + ')}`,
    });
}

/**
 * Format transaction signature for display
 *
 * @param {string} signature - Full signature
 * @returns {string} Shortened signature
 */
export function formatSignature(signature) {
    if (!signature) return '';
    return `${signature.slice(0, 8)}...${signature.slice(-8)}`;
}

/**
 * Get Solana Explorer URL for transaction
 *
 * @param {string} signature - Transaction signature
 * @param {string} network - Network (devnet, mainnet-beta)
 * @returns {string} Explorer URL
 */
export function getExplorerUrl(signature, network = 'devnet') {
    return `https://explorer.solana.com/tx/${signature}?cluster=${network}`;
}
